import { Button, Checkbox, IconButton, TagSelect, Tooltip } from '@ads/components-react';
import { BackspaceIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Skeleton,
  Spinner,
} from '@/shared/ui/atoms';

import { useBannerBrandAllocation } from '../../../hooks/useBannerBrandAllocation';
import { useBannerBrands } from '../../../hooks/useBannerBrands';
import type { BannerClassStudentFormValue } from '../../../types/bannerForm';
import { BANNER_ERROR_ANCHORS } from '../../../utils/bannerFormErrors';
import { BannerFieldLabel } from '../../molecules/BannerFieldLabel';
import { BannerFormSection } from '../../molecules/BannerFormSection';
import { TurmaAutocomplete } from '../../molecules/TurmaAutocomplete';

/** Turmas vinculadas exibidas direto; o excedente fica atrás do accordion. */
const VISIBLE_CLASS_STUDENTS = 5;

export interface BannerClassStudentsSectionProps {
  readonly classStudents: readonly BannerClassStudentFormValue[];
  readonly onAdd: (classStudent: BannerClassStudentFormValue) => void;
  readonly onRemove: (classStudentId: number) => void;
  /** Vincula em bloco as turmas de uma marca, ignorando as já vinculadas. */
  readonly onAddMany: (classStudents: readonly BannerClassStudentFormValue[]) => void;
  /**
   * Desfaz o que uma marca vinculou. Sem `brandId`, desfaz o de todas as marcas —
   * é o que "Limpar filtros" usa.
   */
  readonly onRemoveBrand: (brandId?: number) => void;
  /** `true` quando o formulário foi submetido sem nenhuma turma vinculada. */
  readonly hasError: boolean;
}

/**
 * Bloco "Turmas" do formulário de Banner, exibido apenas quando `Geral` está
 * desmarcado e na aba Português (BR).
 *
 * Nada aqui chama a API de banner: a lista inteira é persistida como
 * `ClassStudentIds` quando o banner é salvo.
 *
 * Os filtros **vinculam em bloco**, não estreitam a busca: marcar uma marca traz
 * todas as turmas dela (respeitando "somente ativas") e desmarcar desfaz o que
 * aquela marca trouxe — o que foi adicionado à mão pelo autocomplete permanece. O
 * autocomplete, por isso, busca em todas as marcas.
 */
export function BannerClassStudentsSection({
  classStudents,
  onAdd,
  onRemove,
  onAddMany,
  onRemoveBrand,
  hasError,
}: BannerClassStudentsSectionProps) {
  const { t } = useTranslation('banner');
  const { brands, isLoading: isLoadingBrands } = useBannerBrands();
  const { fetchByBrand, isAllocating } = useBannerBrandAllocation();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string | undefined>(undefined);
  const [duplicate, setDuplicate] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  // Accordion controlado: o rótulo do gatilho troca entre expandir e reduzir.
  const [openList, setOpenList] = useState('');

  const toggleBrand = async (brandId: number) => {
    const isSelected = brandIds.includes(brandId);
    setBrandIds(current =>
      isSelected ? current.filter(id => id !== brandId) : [...current, brandId],
    );

    if (isSelected) {
      onRemoveBrand(brandId);
      return;
    }

    onAddMany(await fetchByBrand(brandId, activeOnly));
  };

  /**
   * "Somente ativas" muda o conjunto que cada marca vincula, então as marcas
   * selecionadas são revinculadas com o critério novo.
   */
  const changeActiveOnly = async (next: boolean) => {
    setActiveOnly(next);
    if (brandIds.length === 0) return;

    onRemoveBrand();
    const byBrand = await Promise.all(brandIds.map(brandId => fetchByBrand(brandId, next)));
    onAddMany(byBrand.flat());
  };

  const clearFilters = () => {
    setBrandIds([]);
    setActiveOnly(true);
    onRemoveBrand();
  };

  const handleAdd = () => {
    if (selectedId === null) return;
    if (classStudents.some(cs => cs.classStudentId === selectedId)) {
      setDuplicate(true);
      return;
    }
    onAdd({
      classStudentId: selectedId,
      description: selectedLabel,
      ...(selectedBrand !== undefined ? { brandName: selectedBrand } : {}),
    });
    setSelectedId(null);
    setSelectedLabel('');
    setSelectedBrand(undefined);
    setDuplicate(false);
  };

  const hasFilters = brandIds.length > 0 || !activeOnly;
  const visibleClassStudents = classStudents.slice(0, VISIBLE_CLASS_STUDENTS);
  const hiddenClassStudents = classStudents.slice(VISIBLE_CLASS_STUDENTS);

  /** Linha da turma vinculada: descrição, marca e a ação de remover. */
  const renderClassStudent = (classStudent: BannerClassStudentFormValue) => (
    <div className="flex items-center justify-between gap-mini rounded-sm border-hairline border-ctx-layout-border px-micro py-nano">
      <span className="flex flex-col gap-quark">
        <span className="ts-paragraph-xxs text-ctx-content-title">
          {classStudent.description || `#${classStudent.classStudentId}`}
        </span>
        {classStudent.brandName ? (
          <span className="ts-caption-xxs text-ctx-content-base">{classStudent.brandName}</span>
        ) : null}
      </span>
      <IconButton
        icon={TrashIcon}
        ariaLabel={t('banner.form.classStudents.remove')}
        hierarchy="tertiary"
        size="sm"
        className="!text-base-danger-600"
        onClick={() => onRemove(classStudent.classStudentId)}
      />
    </div>
  );

  return (
    <BannerFormSection
      title={t('banner.form.classStudents.heading')}
      hint={t('banner.form.classStudents.hint')}
      errorAnchor={BANNER_ERROR_ANCHORS.ClassStudents}
    >
      <div className="flex items-center gap-quark">
        <Checkbox
          checked={activeOnly}
          size="md"
          label={t('banner.form.classStudents.onlyActive')}
          onCheckedChange={checked => {
            if (checked !== 'indeterminate') void changeActiveOnly(checked);
          }}
        />
        <BannerFieldLabel label="" info={t('banner.form.classStudents.onlyActiveHint')} />
      </div>

      <div className="flex flex-col gap-nano">
        <BannerFieldLabel
          label={t('banner.form.classStudents.brand')}
          info={t('banner.form.classStudents.brandHint')}
        />
        {isLoadingBrands ? (
          <div className="flex items-center gap-nano">
            <Skeleton className="h-xs w-[8rem] rounded-pill" />
            <Skeleton className="h-xs w-[8rem] rounded-pill" />
            <Skeleton className="h-xs w-[8rem] rounded-pill" />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-nano">
            {brands.map(brand => (
              <TagSelect
                key={brand.id}
                label={brand.name}
                size="md"
                mode="filter"
                active={brandIds.includes(brand.id)}
                onClick={() => void toggleBrand(brand.id)}
              />
            ))}
          </div>
        )}

        {isAllocating ? (
          <span className="flex items-center gap-nano ts-caption-xxs text-ctx-content-base">
            <Spinner size="sm" />
            {t('banner.form.classStudents.allocating')}
          </span>
        ) : null}
      </div>

      <div className="flex w-full flex-wrap items-end gap-mini">
        <div className="min-w-[240px] flex-1">
          {/* Sem `brandIds`/`activeOnly`: a busca considera todas as turmas, porque
              os filtros vinculam em bloco em vez de estreitar as opções. */}
          <TurmaAutocomplete
            value={selectedId}
            selectedLabel={selectedLabel}
            onChange={(id, label, brandName) => {
              setSelectedId(id);
              setSelectedLabel(label);
              setSelectedBrand(brandName);
              setDuplicate(false);
            }}
            label={t('banner.form.classStudents.search')}
            placeholder={t('banner.form.classStudents.searchPlaceholder')}
          />
        </div>
        <Button
          type="button"
          hierarchy="primary"
          size="md"
          leadingIcon={PlusIcon}
          disabled={selectedId === null}
          onClick={handleAdd}
        >
          {t('banner.form.classStudents.add')}
        </Button>

        {/* Sempre visível, inativo sem filtro: o operador vê que a ação existe
            antes de precisar dela, e o tooltip diz o que ela faz. */}
        <Tooltip title={t('banner.form.classStudents.clearFilters')} custom={{ root: 'z-[60]' }}>
          <IconButton
            type="button"
            icon={BackspaceIcon}
            ariaLabel={t('banner.form.classStudents.clearFilters')}
            hierarchy="secondary"
            size="md"
            disabled={!hasFilters}
            onClick={clearFilters}
          />
        </Tooltip>
      </div>

      {duplicate ? (
        <p className="ts-caption-xxs text-base-danger-700">
          {t('banner.form.classStudents.duplicate')}
        </p>
      ) : null}

      <div className="flex flex-col gap-nano">
        <div className="flex items-center justify-between">
          <span className="ts-subtitle-xxs text-ctx-content-title">
            {t('banner.form.classStudents.listHeading')}
          </span>
          <span className="ts-caption-xxs text-ctx-content-base">
            {classStudents.length > 0
              ? t('banner.form.classStudents.summary', { count: classStudents.length })
              : t('banner.form.classStudents.summaryNone')}
          </span>
        </div>

        {classStudents.length > 0 ? (
          <>
            <ul className="flex flex-col gap-quark">
              {visibleClassStudents.map(classStudent => (
                <li key={classStudent.classStudentId}>{renderClassStudent(classStudent)}</li>
              ))}
            </ul>

            {/* Vincular uma marca traz turmas às dezenas: as primeiras
                cinco ficam à vista e o resto espera atrás do accordion, para a lista
                não empurrar o resto do formulário para fora da tela. */}
            {hiddenClassStudents.length > 0 ? (
              <Accordion type="single" collapsible value={openList} onValueChange={setOpenList}>
                <AccordionItem value="hidden-class-students">
                  <AccordionTrigger className="justify-between gap-mini rounded-sm border-hairline border-ctx-layout-border px-micro py-nano ts-subtitle-xxs text-ctx-content-title">
                    {openList
                      ? t('banner.form.classStudents.showLess')
                      : t('banner.form.classStudents.showMore', {
                          count: hiddenClassStudents.length,
                        })}
                    <ChevronDownIcon className="h-xxxs w-xxxs shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="flex flex-col gap-quark pt-quark">
                      {hiddenClassStudents.map(classStudent => (
                        <li key={classStudent.classStudentId}>
                          {renderClassStudent(classStudent)}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : null}
          </>
        ) : (
          <p
            className={`ts-caption-xxs ${hasError ? 'text-base-danger-700' : 'text-ctx-content-base'}`}
          >
            {t('banner.form.classStudents.empty')}
          </p>
        )}
      </div>
    </BannerFormSection>
  );
}
